import axios from '@/config/axios';
import { useToast } from 'vue-toastification';

// ═══════════════════════════════════════════════════════════════════════════
// API — /translator/v2/...
// Sửa endpoint tại đây khi backend thay đổi.
// ═══════════════════════════════════════════════════════════════════════════
function unwrap(res) {
  const { status, message, result } = res.data;
  if (status !== '200') throw new Error(message || 'Có lỗi xảy ra từ máy chủ.');
  return result;
}

export const translatorApi = {
  /** GET /translator/v2/get-list?page=<page> */
  getList: (page = 0, status = null) => {
    const params = { page };
    if (status !== null && status !== undefined) params.status = status;
    return axios.get('translator/v2/get-list', { params }).then(unwrap);
  },

  /** GET /translator/v2/search?page=<page>&key=<key> */
  search: (page = 0, key = '', status = null) => {
    const params = { page };
    if (key) params.key = key;
    if (status !== null && status !== undefined) params.status = status;
    return axios.get('translator/v2/search', { params }).then(unwrap);
  },

  /** POST /translator/v2/create  — { name } */
  create: (payload) =>
    axios.post('translator/v2/create', payload).then(unwrap),

  /** GET /translator/v2/get-by-id/:id */
  getById: (id) =>
    axios.get(`translator/v2/get-by-id/${id}`).then(unwrap),

  /** POST /translator/v2/update */
  update: (payload) =>
    axios.post('translator/v2/update', payload).then(unwrap),

  /** POST /translator/v2/delete */
  remove: (payload) =>
    axios.post('translator/v2/delete', payload).then(unwrap),

  /** POST /translator/v2/restore */
  restore: (payload) =>
    axios.post('translator/v2/restore', payload).then(unwrap),

  /** GET /api/history/v2/translator/get-by-id/{id} */
  getHistory: (id) =>
    axios.get(`history/v2/translator/get-by-id/${id}`).then(res => {
      const { status, message, result } = res.data;
      if (status === '404') return [];
      if (status !== '200') throw new Error(message || 'Có lỗi xảy ra từ máy chủ.');
      return result;
    }),
};
// ═══════════════════════════════════════════════════════════════════════════

const toast = {
  success(msg, options = {}) {
    return useToast().success(msg, { position: "top-center", ...options });
  },
  error(msg, options = {}) {
    return useToast().error(msg, { position: "top-center", ...options });
  }
};

export default {
  name: 'AdminTranslators',
  data() {
    return {
      translators: [],
      searchQuery: '',
      currentPage: 0,
      totalPages: 1,
      totalElements: 0,
      pageSize: 10,
      isLoading: false,
      isAdmin: false,
      modalVisible: false,
      isEditMode: false,
      deleteModalVisible: false,
      selectedTranslator: null,
      deleteReason: '',
      restoreModalVisible: false,
      selectedTranslatorForRestore: null,
      restoreReason: '',
      form: { id: null, name: '', updateTime: '', reason: '' },
      errors: { name: '', reason: '', deleteReason: '', restoreReason: '' },
      reasonModalVisible: false,
      isModalLoading: false,
      similarTranslators: [],
      isSearchingSimilar: false,
      checkDoneOnce: false,
      userHasChecked: false,
      originalName: '',
      searchTimeout: null,
      historyModalVisible: false,
      historyList: [],
      selectedTranslatorForHistory: null,
      isHistoryLoading: false
    };
  },
  computed: {
    filteredTranslators() {
      return [...this.translators];
    },
    hasExactDuplicate() {
      const name = this.form.name.trim().toLowerCase();
      return this.similarTranslators.some(t => t.name.trim().toLowerCase() === name);
    },
    isUnchanged() {
      if (!this.isEditMode) return false;
      return (this.form.name || '').trim().toUpperCase() === (this.originalName || '').trim().toUpperCase();
    },
    visiblePages() {
      const range = [];
      const maxVisible = 5;
      let start = Math.max(0, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible);

      if (end - start < maxVisible) {
        start = Math.max(0, end - maxVisible);
      }

      for (let i = start; i < end; i++) {
        range.push(i);
      }
      return range;
    }
  },
  watch: {
    'form.name'(newVal) {
      this.handleNameChange(newVal);
      this.checkDoneOnce = false;
      this.userHasChecked = false;
    },

  },
  mounted() {
    this.checkUserRole();
    this.reloadData();
  },
  methods: {
    // ── SEARCH ───────────────────────────────────────────────────────────
    /** Được gọi từ @input (native) — bypass IME composition guard của Vue */
    onSearchInput(val) {
      this.searchQuery = val;
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => this.reloadData(0), 800);
    },
    searchNow() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = null;
      }
      this.reloadData(0);
    },

    // ── DATA ───────────────────────────────────────────────────────────────
    reloadData(page = 0) {
      this.isLoading = true;
      this.currentPage = page;

      const call = this.searchQuery.trim()
        ? translatorApi.search(page, this.searchQuery.trim())
        : translatorApi.getList(page);

      call
        .then((result) => {
          this.translators   = result.content || [];
          this.totalPages    = result.totalPages || 1;
          this.totalElements = result.totalElements || 0;
          this.pageSize      = result.size || result.pageable?.pageSize || 10;
        })
        .catch(err => {
          const msg = err.message || '';
          if (msg.includes('Không có') || msg.includes('không tìm thấy') || msg.includes('not found') || msg.includes('Chưa có')) {
            this.translators = [];
            this.totalPages = 1;
            this.totalElements = 0;
          } else {
            toast.error(err.message || 'Có lỗi xảy ra khi tải danh sách dịch giả.');
          }
        })
        .finally(() => { this.isLoading = false; });
    },

    changePage(page) {
      if (page < 0 || page >= this.totalPages) return;
      this.reloadData(page);
    },

    onSearch() { this.reloadData(0); },

    handleNameChange(val) {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      // Khi tên thay đổi, xóa kết quả cũ để tránh hiển thị sai
      this.similarTranslators = [];
    },

    /** Gọi thủ công khi bấm button "Kiểm tra" */
    checkSimilarNow(showToast = true) {
      this.similarTranslators = [];
      this.errors.name = '';
      if (!this.validateForm(false)) {
        return Promise.resolve(false);
      }
      const query = (this.form.name || '').trim();
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.isSearchingSimilar = true;
      return translatorApi.search(0, query)
        .then(res => {
          const matches = res.content || res || [];
          this.similarTranslators = matches.filter(t => t.id !== this.form.id);
          
          const isDup = this.similarTranslators.some(
            t => t.name.trim().toUpperCase() === query.toUpperCase()
          );
          if (isDup) {
            toast.error('Dịch giả này đã tồn tại trong hệ thống!');
            return false;
          } else {
            if (showToast) {
              toast.success('Kiểm tra hoàn tất: Dữ liệu hợp lệ!');
            }
            return true;
          }
        })
        .catch(err => {
          const msg = err.message || '';
          if (msg.includes('Không có') || msg.includes('không tìm thấy') || msg.includes('not found')) {
            this.similarTranslators = [];
            if (showToast) {
              toast.success('Kiểm tra hoàn tất: Dữ liệu hợp lệ!');
            }
            return true;
          } else {
            console.error('Error checking similar translators:', err);
            toast.error('Có lỗi xảy ra khi kiểm tra trùng lặp.');
            return false;
          }
        })
        .finally(() => {
          this.isSearchingSimilar = false;
          this.checkDoneOnce = true;
          this.userHasChecked = true;
        });
    },

    // ── MODAL ──────────────────────────────────────────────────────────────
    openAddModal() {
      this.isEditMode = false;
      this.form = { id: null, name: '', updateTime: '', reason: '' };
      this.errors = { name: '', reason: '', deleteReason: '' };
      this.similarTranslators = [];
      this.isSearchingSimilar = false;
      this.isModalLoading = false;
      this.checkDoneOnce = false;
      this.userHasChecked = false;
      this.originalName = '';
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = null;
      }
      this.modalVisible = true;
      this.$nextTick(() => this.$refs.nameInput?.focus());
    },

    openEditModal(trans) {
      this.isEditMode = true;
      this.modalVisible = true;
      this.isModalLoading = true;
      this.form = { id: trans.id, name: '', updateTime: '', reason: '' };
      this.errors = { name: '', reason: '', deleteReason: '' };
      this.similarTranslators = [];
      this.isSearchingSimilar = false;
      this.checkDoneOnce = false;
      this.userHasChecked = false;
      this.originalName = '';
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = null;
      }

      translatorApi.getById(trans.id)
        .then(data => {
          this.form.name = data.name || '';
          this.originalName = data.name || '';
          this.form.updateTime = data.updateTime || '';
          this.$nextTick(() => {
            this.$refs.nameInput?.focus();
          });
        })
        .catch(err => {
          toast.error(err.message || 'Không thể lấy thông tin chi tiết dịch giả.');
          this.closeModal();
        })
        .finally(() => {
          this.isModalLoading = false;
        });
    },

    closeModal() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = null;
      }
      this.modalVisible = false;
      this.similarTranslators = [];
      this.isSearchingSimilar = false;
      this.checkDoneOnce = false;
    },

    closeReasonModal() {
      this.reasonModalVisible = false;
      this.form.reason = '';
      this.errors.reason = '';
    },

    // ── VALIDATION ─────────────────────────────────────────────────────────
    validateForm(checkDup = true) {
      let isValid = true;
      this.errors.name = '';
      if (!this.form.name || !this.form.name.trim()) {
        this.errors.name = 'Tên dịch giả không được bỏ trống.'; isValid = false;
      } else {
        const trimmedName = this.form.name.trim();
        this.form.name = trimmedName;
        const trimmedUpper = trimmedName.toUpperCase();
        if (trimmedUpper.length > 100) {
          this.errors.name = 'Tên dịch giả không được quá 100 ký tự.'; isValid = false;
        } else if (checkDup) {
          let isDup = this.translators.some(
            t => t.name.toUpperCase().trim() === trimmedUpper && t.id !== this.form.id
          );
          if (!isDup) {
            isDup = this.similarTranslators.some(
              t => t.name.toUpperCase().trim() === trimmedUpper && t.id !== this.form.id
            );
          }
          if (isDup) { this.errors.name = 'Dịch giả này đã tồn tại trong hệ thống.'; isValid = false; }
        }
      }
      return isValid;
    },

    // ── SAVE ───────────────────────────────────────────────────────────────
    saveTranslator() {
      if (this.isLoading) return;
      // Chặn bypass phím Enter: Buộc phải hoàn tất kiểm tra trùng lặp trên API trước
      if (!this.checkDoneOnce || this.hasExactDuplicate) {
        this.checkSimilarNow();
        return;
      }
      if (!this.validateForm()) return;

      if (this.isEditMode) {
        this.errors.reason = '';
        this.form.reason = '';
        this.reasonModalVisible = true;
        this.$nextTick(() => this.$refs.reasonInput?.focus());
        return;
      }

      const payload = { name: this.form.name };
      this.isLoading = true;

      translatorApi.create(payload)
        .then(() => {
          toast.success('Thêm dịch giả mới thành công!');
          this.closeModal();
          this.reloadData(this.currentPage);
        })
        .catch(err => toast.error(err.message || 'Có lỗi xảy ra.'))
        .finally(() => { this.isLoading = false; });
    },

    submitUpdateWithReason() {
      if (this.isLoading) return;
      const reasonVal = (this.form.reason || '').trim();
      if (!reasonVal) {
        this.errors.reason = 'Lý do chỉnh sửa không được bỏ trống.';
        return;
      }
      if (reasonVal.length < 10) {
        this.errors.reason = 'Lý do chỉnh sửa phải có ít nhất 10 ký tự.';
        return;
      }
      if (reasonVal.length > 500) {
        this.errors.reason = 'Lý do chỉnh sửa không được quá 500 ký tự.';
        return;
      }

      this.errors.reason = '';
      this.reasonModalVisible = false;
      this.modalVisible = false;

      const payload = {
        name: this.form.name,
        updateTime: this.form.updateTime || '',
        reason: reasonVal
      };
      this.isLoading = true;

      translatorApi.update({ id: this.form.id, ...payload })
        .then(() => {
          toast.success('Cập nhật thông tin dịch giả thành công!');
          this.reloadData(this.currentPage);
        })
        .catch(err => {
          toast.error(err.message || 'Có lỗi xảy ra.');
          this.reloadData(this.currentPage);
        })
        .finally(() => { this.isLoading = false; });
    },

    // ── DELETE ─────────────────────────────────────────────────────────────
    confirmDelete(trans) {
      this.selectedTranslator = trans;
      this.deleteReason = '';
      this.errors.deleteReason = '';
      this.deleteModalVisible = true;
      this.$nextTick(() => this.$refs.deleteReasonInput?.focus());
    },
    closeDeleteModal() {
      this.deleteModalVisible = false;
      this.selectedTranslator = null;
      this.deleteReason = '';
      this.errors.deleteReason = '';
    },

    deleteTranslator() {
      if (!this.selectedTranslator || this.isLoading) return;
      const reasonVal = (this.deleteReason || '').trim();
      if (!reasonVal) {
        this.errors.deleteReason = 'Lý do xóa không được bỏ trống.';
        return;
      }
      if (reasonVal.length < 10) {
        this.errors.deleteReason = 'Lý do xóa phải có ít nhất 10 ký tự.';
        return;
      }
      if (reasonVal.length > 500) {
        this.errors.deleteReason = 'Lý do xóa không được quá 500 ký tự.';
        return;
      }

      this.errors.deleteReason = '';
      this.isLoading = true;
      translatorApi.remove({
        id: this.selectedTranslator.id,
        reason: reasonVal,
        updateTime: this.selectedTranslator.updateTime || this.selectedTranslator.update_time || ''
      })
        .then((res) => {
          const msg = res?.message || (this.isAdmin ? `Đã xóa dịch giả "${this.selectedTranslator.name}" thành công!` : 'Đã yêu cầu xóa cho dịch giả này');
          toast.success(msg);
          this.closeDeleteModal();
          this.reloadData(this.currentPage);
        })
        .catch(err => {
          toast.error(err.message || 'Có lỗi xảy ra khi xóa.');
          this.closeDeleteModal();
          this.reloadData(this.currentPage);
        })
        .finally(() => { this.isLoading = false; });
    },

    // ── RESTORE ────────────────────────────────────────────────────────────
    confirmRestore(trans) {
      this.selectedTranslatorForRestore = trans;
      this.restoreReason = '';
      this.errors.restoreReason = '';
      this.restoreModalVisible = true;
      this.$nextTick(() => this.$refs.restoreReasonInput?.focus());
    },
    closeRestoreModal() {
      this.restoreModalVisible = false;
      this.selectedTranslatorForRestore = null;
      this.restoreReason = '';
      this.errors.restoreReason = '';
    },

    restoreTranslator() {
      if (!this.selectedTranslatorForRestore || this.isLoading) return;
      const reasonVal = (this.restoreReason || '').trim();
      const label = this.isAdmin ? 'Lý do khôi phục' : 'Lý do yêu cầu khôi phục';
      if (!reasonVal) {
        this.errors.restoreReason = `${label} không được bỏ trống.`;
        return;
      }
      if (reasonVal.length < 10) {
        this.errors.restoreReason = `${label} phải có ít nhất 10 ký tự.`;
        return;
      }
      if (reasonVal.length > 500) {
        this.errors.restoreReason = `${label} không được quá 500 ký tự.`;
        return;
      }

      this.errors.restoreReason = '';
      this.isLoading = true;
      const isUserAdmin = this.isAdmin;
      const transName = this.selectedTranslatorForRestore.name;
      translatorApi.restore({
        id: this.selectedTranslatorForRestore.id,
        reason: reasonVal,
        updateTime: this.selectedTranslatorForRestore.updateTime || this.selectedTranslatorForRestore.update_time || ''
      })
        .then(() => {
          toast.success(
            isUserAdmin
              ? `Đã khôi phục dịch giả "${transName}" thành công!`
              : `Đã gửi yêu cầu khôi phục dịch giả "${transName}" thành công!`
          );
          this.closeRestoreModal();
          this.reloadData(this.currentPage);
        })
        .catch(err => {
          toast.error(err.message || 'Có lỗi xảy ra khi khôi phục.');
          this.closeRestoreModal();
          this.reloadData(this.currentPage);
        })
        .finally(() => { this.isLoading = false; });
    },

    checkUserRole() {
      const currentAccountStr = localStorage.getItem('current-account');
      if (currentAccountStr) {
        try {
          const currentAccount = JSON.parse(currentAccountStr);
          if (currentAccount.authority) {
            let roles = typeof currentAccount.authority === 'string' ? JSON.parse(currentAccount.authority) : currentAccount.authority;
            if (!Array.isArray(roles)) roles = [roles];
            this.isAdmin = roles.some(r => {
              const name = String(r.authority || r).toUpperCase();
              return name === 'ROLE_ADMIN' || name === 'ADMIN';
            });
          }
        } catch (e) {
          this.isAdmin = false;
        }
      }
    },

    // ── HISTORY ────────────────────────────────────────────────────────────
    openHistoryModal(trans) {
      this.selectedTranslatorForHistory = trans;
      this.historyModalVisible = true;
      this.isHistoryLoading = true;
      this.historyList = [];
      translatorApi.getHistory(trans.id)
        .then(res => {
          this.historyList = res || [];
        })
        .catch(err => {
          toast.error(err.message || 'Có lỗi xảy ra khi tải lịch sử dịch giả.');
        })
        .finally(() => {
          this.isHistoryLoading = false;
        });
    },

    closeHistoryModal() {
      this.historyModalVisible = false;
      this.selectedTranslatorForHistory = null;
      this.historyList = [];
    },

    getHistoryActionLabel(type) {
      if (type === 1) return 'Thêm mới';
      if (type === 2) return 'Cập nhật';
      if (type === 3) return 'Xóa bỏ';
      return 'Không xác định';
    },

    getHistoryBadgeClass(type) {
      if (type === 1) return 'badge-init';
      if (type === 2) return 'badge-update';
      if (type === 3) return 'badge-danger';
      return 'badge-status';
    },

    getHistoryIcon(type) {
      if (type === 1) return 'fa-solid fa-plus';
      if (type === 2) return 'fa-solid fa-pen';
      if (type === 3) return 'fa-solid fa-trash';
      return 'fa-solid fa-circle-info';
    },

    formatVersionTime(version) {
      if (!version) return 'N/A';
      const str = String(version).trim();
      if (/^\d{14}$/.test(str)) {
        const y = str.substring(0, 4);
        const m = str.substring(4, 6);
        const d = str.substring(6, 8);
        const h = str.substring(8, 10);
        const min = str.substring(10, 12);
        const sec = str.substring(12, 14);
        return `${h}:${min}:${sec} ${d}/${m}/${y}`;
      }
      if (/^\d{12}$/.test(str)) {
        const y = str.substring(0, 4);
        const m = str.substring(4, 6);
        const d = str.substring(6, 8);
        const h = str.substring(8, 10);
        const min = str.substring(10, 12);
        return `${h}:${min}:00 ${d}/${m}/${y}`;
      }
      if (/^\d{2}:\d{2}\s+\d{2}\/\d{2}\/\d{4}$/.test(str)) {
        const parts = str.split(/\s+/);
        return `${parts[0]}:00 ${parts[1]}`;
      }
      return str;
    },

    isRestoreItem(item) {
      if (!item) return false;
      if (item.status === 3) return true;
      if (item.status !== 1) return false;
      const r = (item.reason || item.requestReason || '').toLowerCase();
      return r.includes('khôi phục');
    },

    getStatusLabel(item) {
      if (!item) return 'Phát hành';
      if (item.status === 2) return 'Đã xóa';
      if (item.status === 3) return 'Chờ khôi phục';
      if (item.status === 1) {
        return this.isRestoreItem(item) ? 'Chờ khôi phục' : 'Chờ xóa';
      }
      return 'Phát hành';
    },

    getStatusTooltip(item) {
      if (!item) return 'Trạng thái: Đang phát hành';
      if (item.status === 2) return 'Trạng thái: Đã xóa';
      if (item.status === 3) return 'Trạng thái: Đang chờ khôi phục';
      if (item.status === 1) {
        return this.isRestoreItem(item) ? 'Trạng thái: Đang chờ khôi phục' : 'Trạng thái: Đang chờ xóa';
      }
      return 'Trạng thái: Đang phát hành';
    },

    getStatusBadgeClass(item) {
      if (!item || item.status === 0 || item.status === undefined || item.status === null) return 'status-badge-pub';
      if (item.status === 3) return 'status-badge-pending-restore';
      if (item.status === 1) return this.isRestoreItem(item) ? 'status-badge-pending-restore' : 'status-badge-pending-del';
      if (item.status === 2) return 'status-badge-deleted';
      return 'status-badge-pub';
    },

    getHistoryStatusLabel(status) {
      if (status === 0 || status === undefined || status === null) return 'Đang phát hành';
      if (status === 1) return 'Đang chờ xóa';
      if (status === 2) return 'Đã xóa';
      return 'Không xác định';
    },

    getHistoryStatusClass(status) {
      if (status === 0 || status === undefined || status === null) return 'status-badge-pub';
      if (status === 1) return 'status-badge-pending-del';
      if (status === 2) return 'status-badge-deleted';
      return '';
    }
  }
};
